import request from '@/utils/request';
import type { CreateSpaceParams, GetSpaceImageListParams, SpaceImageRes, SpaceInfo, SpaceRepoListParams, SpaceRepoListRes, SpaceRelatedRepos, SpaceServerRes, SpaceStartConfig, StartSpaceParams, RelateRepoParams, IndustryTag } from './types';
import type { commonRepoResType } from '@/api/repo/types';
import { reqCatchV2 } from '@/utils/catch';
export function createSpace(data: CreateSpaceParams) {
  return reqCatchV2<commonRepoResType>(() => request({
    url: '/aihub/api/v1/space',
    method: 'post',
    data
  }));
}

export function getSpaceServerList(data?: {
  page: number,
  per_page: number,
  ai_device_type?: string
}) {
  return reqCatchV2<SpaceServerRes>(() => request({
    url: '/aihub/api/v1/space/server_list',
    method: 'get',
    params: data
  }));
}

export function getSpaceImageList(data?: GetSpaceImageListParams) {
  return reqCatchV2<SpaceImageRes>(() => request({
    url: '/aihub/api/v1/space/image_list',
    method: 'get',
    params: data
  }));
}

export function getSpaceRelatedRepos(data: { projectId: number }) {
  return reqCatchV2<SpaceRelatedRepos>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/get_related_repo_list`,
    method: 'get'
  }));
}

export function relateRepos(data: { projectId: number } & RelateRepoParams) {
  return reqCatchV2(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/related_repo`,
    method: 'post',
    data
  }));
}

// 启动时的配置
export function getSpaceStartConfig(data: { projectId: number }) {
  return reqCatchV2<SpaceStartConfig>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/get_start_config`,
    method: 'get'
  }));
}

export function startSpace(data: { projectId: number } & StartSpaceParams) {
  return reqCatchV2(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/start`,
    method: 'post',
    data
  }));
}

export function getSpaceRepoList(data: SpaceRepoListParams & { user_name: string }) {
  return reqCatchV2<SpaceRepoListRes>(() => request({
    url: `/api/v2/projects/profile/${data.user_name}/projects`,
    method: 'get',
    params: data
  }));
}

/** 暂停space */
export function spacePause(data: {
  projectId : string | number
}): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/aihub/api/v1/space/${data.projectId}/pause`,
        method: 'post'
      })
  );
}
/** 重置space */
export function spaceReset(data: {
  projectId : string | number
}): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/aihub/api/v1/space/${data.projectId}/reset`,
        method: 'post'
      })
  );
}
/** 转移space */
export function spaceTransfer(data: {
  spaceId: string | number,
  namespace: string | number
}): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/aihub/api/v1/space/${data.spaceId}/transfer`,
        method: 'put',
        data: {
          namespace: data.namespace
        }
      })
  );
}
/** 删除space */
export function spaceDelete(data: {
  projectId : string | number
}): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/aihub/api/v1/space/${data.projectId}`,
        method: 'delete'
      })
  );
}

/** 设置space资源服务 */
export function setSpaceServer(data: {
  project_id: string | number,
  server_id: string | number
}): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/aihub/api/v1/space/${data.project_id}/update_server`,
        method: 'post',
        data: {
          server_id: data.server_id
        }
      })
  );
}

/** 获取space用量 */
export function getSpaceUsage(namespace: string): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `aihub/api/v1/space_usage/${namespace}`,
        method: 'get'
      })
  );
}

/** 获取token用量 */
export function getTokenUsage(): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `/widget/api/v1/token_usage`,
        method: 'get'
      })
  );
}

/** space基础设置 */
export function spaceBaseSetting(data: Partial<commonRepoResType> & {projectId: string | number}): Promise<any> {
  const { projectId, ...conf } = data;
  return reqCatchV2(
    () =>
      request({
        url: `aihub/api/v1/space/setting/base/${projectId}`,
        method: 'put',
        data: conf
      })
  );
}
/** space分支设置 */
export function spaceBranchSetting(data: Partial<commonRepoResType> & {projectId: string | number}): Promise<any> {
  const { projectId, ...conf } = data;
  return reqCatchV2(
    () =>
      request({
        url: `aihub/api/v1/space/setting/${projectId}`,
        method: 'put',
        data: conf
      })
  );
}
/** 获取space设置 */
export function getSpaceSetting(projectId: string | number): Promise<any> {
  return reqCatchV2(
    () =>
      request({
        url: `aihub/api/v1/space/setting/${projectId}`,
        method: 'get'
      })
  );
}
export function getSpaceLogs(data: { projectId: number }) {
  return reqCatchV2<{logs?: string[]}>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/get_logs`,
    method: 'get'
  }));
}

export function getSpaceEvents(data: { projectId: number }) {
  return reqCatchV2<{ events?: {message:string}[]}>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/get_events`,
    method: 'get'
  }));
}

export function getSpaceInfo(data: { projectId: number }) {
  return reqCatchV2<SpaceInfo>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}`,
    method: 'get'
  }));
}

export function getIndustryTag() {
  return reqCatchV2<IndustryTag[]>(() => request({
    url: `/aihub/api/v1/space/setting/labels`,
    method: 'get'
  }));
}

export function getRepoFullInfo(data: { projectId: string, statistics?: boolean }) {
  return reqCatchV2(() => request({
    url: `/api/v4/projects/${data.projectId}`,
    method: 'get',
    params: data
  }));
}

// space status单独接口，适用于状态轮询
export function getSpaceStatus(data: { projectId: number; }) {
  return reqCatchV2<Pick<SpaceInfo, 'status'>>(() => request({
    url: `/aihub/api/v1/space/${data.projectId}/get_status`,
    method: 'get'
  }));
}
// space star
export function spaceStarApi(data: {repoId: string | number}): Promise<any> {
  return request({
    url: `/aihub/api/v1/space/${data.repoId}/star`,
    method: 'post'
  });
}
// space unstar
export function spaceUnStarApi(data: {repoId: string | number}): Promise<any> {
  return request({
    url: `/aihub/api/v1/space/${data.repoId}/un_star`,
    method: 'post'
  });
}
// space fork
export function spaceForkApi(data: any): Promise<any> {
  const { projectId, ...conf } = data;
  return request({
    url: `/aihub/api/v1/space/${projectId}/fork`,
    method: 'post',
    data: conf
  });
}

// space 休眠时间列表  /api/v1/space/ttl_list/{project_id}
export function getSpaceSleepTimeList(data: { projectId: number | undefined; }) {
  return request({
    url: `/aihub/api/v1/space/ttl_list/${data.projectId}`,
    method: 'get'
  });
}

// 判断能否切换资源
export function updateServerCheck(data: { projectId: number | undefined; }) {
  return request({
    url: `/aihub/api/v1/space/${data.projectId}/update_server_check`,
    method: 'get'
  });
}

// 获取项目关联spaceId
export function getRepoSpace(data: { project_id: number | undefined; pre_page: number }) {
  return reqCatchV2(() => request({
    url: `/aihub/api/v1/projects_widget/get_widget`,
    method: 'get',
    params: data
  }));
}
